import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Video as VideoIcon,
  Calendar,
  Tag,
  X,
  Save,
  Eye,
  EyeOff,
  PlayCircle,
  Link as LinkIcon,
  ExternalLink,
  Loader2,
  Image,
  Upload
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const categories = ['Exercise', 'Nutrition', 'Therapy', 'Stretching', 'Wellness'];

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const fileInputRef = useRef(null);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const emptyVideo = {
    title: '',
    slug: '',
    category: 'Exercise',
    video_url: '',
    thumbnail_url: '',
    description: '',
    duration: '',
    published: false
  };

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/videos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const getYouTubeThumbnail = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
    return null;
  };

  const openEditor = (video = null) => {
    setCurrentVideo(video || { ...emptyVideo });
    setIsEditing(true);
  };

  const closeEditor = () => {
    setIsEditing(false);
    setCurrentVideo(null);
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentVideo({ ...currentVideo, thumbnail_url: data.url });
        toast.success('Thumbnail uploaded');
      } else {
        toast.error('Failed to upload thumbnail');
      }
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTitleChange = (title) => {
    const updates = { title };
    if (!currentVideo.id || !currentVideo.slug) {
      updates.slug = generateSlug(title);
    }
    setCurrentVideo({ ...currentVideo, ...updates });
  };

  const handleVideoUrlChange = (url) => {
    const updates = { video_url: url };
    // Auto-generate thumbnail if not custom set
    if (!currentVideo.thumbnail_url || currentVideo.thumbnail_url.includes('img.youtube.com')) {
      const thumbnail = getYouTubeThumbnail(url);
      if (thumbnail) {
        updates.thumbnail_url = thumbnail;
      }
    }
    setCurrentVideo({ ...currentVideo, ...updates });
  };

  const handleSave = async () => {
    if (!currentVideo.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!currentVideo.video_url.trim()) {
      toast.error('Video URL is required');
      return;
    }

    setSaving(true);
    try {
      const isNew = !currentVideo.id;
      const url = isNew 
        ? `${API_URL}/api/admin/videos`
        : `${API_URL}/api/admin/videos/${currentVideo.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: currentVideo.title,
          slug: currentVideo.slug || null,
          category: currentVideo.category,
          video_url: currentVideo.video_url,
          thumbnail_url: currentVideo.thumbnail_url || null,
          description: currentVideo.description,
          duration: currentVideo.duration || '',
          published: currentVideo.published
        })
      });

      if (response.ok) {
        await fetchVideos();
        closeEditor();
        toast.success(isNew ? 'Video added successfully' : 'Video updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save video');
      }
    } catch (error) {
      console.error('Failed to save video:', error);
      toast.error('Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/videos/${videoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setVideos(videos.filter(v => v.id !== videoId));
        toast.success('Video deleted successfully');
      } else {
        toast.error('Failed to delete video');
      }
    } catch (error) {
      console.error('Failed to delete video:', error);
      toast.error('Failed to delete video');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-sage-600 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-videos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl font-light text-charcoal mb-2">Video Management</h1>
          <p className="text-charcoal/60">Add and manage video content</p>
        </div>
        <Button
          onClick={() => openEditor()}
          className="bg-sage-600 hover:bg-sage-700 text-white rounded-xl gap-2"
          data-testid="new-video-btn"
        >
          <Plus className="w-5 h-5" />
          Add Video
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="bg-white border-0 shadow-soft rounded-xl">
          <CardContent className="p-5 text-center">
            <div className="text-2xl font-serif text-charcoal">{videos.length}</div>
            <div className="text-sm text-charcoal/60">Total Videos</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-0 shadow-soft rounded-xl">
          <CardContent className="p-5 text-center">
            <div className="text-2xl font-serif text-green-700">{videos.filter(v => v.published).length}</div>
            <div className="text-sm text-green-600">Published</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-0 shadow-soft rounded-xl">
          <CardContent className="p-5 text-center">
            <div className="text-2xl font-serif text-amber-700">{videos.filter(v => !v.published).length}</div>
            <div className="text-sm text-amber-600">Drafts</div>
          </CardContent>
        </Card>
      </div>

      {/* Videos List */}
      {videos.length === 0 ? (
        <Card className="bg-white border-0 shadow-soft rounded-xl">
          <CardContent className="p-12 text-center">
            <VideoIcon className="w-12 h-12 text-charcoal/20 mx-auto mb-4" />
            <p className="text-charcoal/60 mb-4">No videos yet</p>
            <Button onClick={() => openEditor()} className="bg-sage-600 hover:bg-sage-700 text-white rounded-xl">
              Add Your First Video
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl overflow-hidden">
                {/* Thumbnail */}
                <div className="relative aspect-video bg-sage-100">
                  {video.thumbnail_url ? (
                    <img 
                      src={video.thumbnail_url.startsWith('/api') ? `${API_URL}${video.thumbnail_url}` : video.thumbnail_url} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <PlayCircle className="w-12 h-12 text-sage-300" />
                    </div>
                  )}
                  {video.duration && (
                    <span className="absolute bottom-2 right-2 bg-charcoal/80 text-white text-xs px-2 py-1 rounded">
                      {video.duration}
                    </span>
                  )}
                  <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded font-medium ${
                    video.published 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {video.published ? 'Published' : 'Draft'}
                  </span>
                </div>

                <CardContent className="p-5">
                  <h3 className="font-semibold text-charcoal mb-2 line-clamp-1">{video.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-charcoal/60 mb-2">
                    <span className="flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {video.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(video.created_at)}
                    </span>
                  </div>
                  <p className="text-xs text-charcoal/50 mb-3 flex items-center gap-1">
                    <LinkIcon className="w-3 h-3" />
                    /resources/videos/{video.slug}
                  </p>
                  
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-border/30">
                    {video.published && (
                      <a
                        href={`/resources/videos/${video.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-charcoal/60 hover:text-sage-600 transition-colors"
                        title="View video"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditor(video)}
                      className="text-charcoal/60 hover:text-charcoal"
                      data-testid={`edit-video-${video.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteVideo(video.id)}
                      className="text-charcoal/60 hover:text-red-600"
                      data-testid={`delete-video-${video.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {isEditing && currentVideo && (
        <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-border/50 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-serif text-2xl text-charcoal">
                {currentVideo.id ? 'Edit Video' : 'Add Video'}
              </h2>
              <button
                onClick={closeEditor}
                className="p-2 hover:bg-cream-50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Title *</label>
                <Input
                  value={currentVideo.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Video title"
                  className="h-11 bg-cream-50 border-border/50 rounded-xl"
                  data-testid="video-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  Slug
                  <span className="text-charcoal/50 font-normal ml-2">/resources/videos/{currentVideo.slug || 'your-slug'}</span>
                </label>
                <Input
                  value={currentVideo.slug}
                  onChange={(e) => setCurrentVideo({ ...currentVideo, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="custom-url-slug"
                  className="h-11 bg-cream-50 border-border/50 rounded-xl"
                  data-testid="video-slug-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Video URL *</label>
                <Input
                  value={currentVideo.video_url}
                  onChange={(e) => handleVideoUrlChange(e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="h-11 bg-cream-50 border-border/50 rounded-xl"
                  data-testid="video-url-input"
                />
                <p className="text-xs text-charcoal/50 mt-1">YouTube or Vimeo URL. Thumbnail will be auto-generated for YouTube.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  Thumbnail
                  <span className="text-charcoal/50 font-normal ml-2">(auto-generated for YouTube, or upload custom)</span>
                </label>
                {/* Thumbnail preview */}
                {currentVideo.thumbnail_url ? (
                  <div className="mb-3 relative rounded-xl overflow-hidden bg-sage-100 aspect-video" data-testid="video-thumbnail-preview">
                    <img 
                      src={currentVideo.thumbnail_url.startsWith('/api') ? `${API_URL}${currentVideo.thumbnail_url}` : currentVideo.thumbnail_url} 
                      alt="Thumbnail preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setCurrentVideo({ ...currentVideo, thumbnail_url: '' })}
                      className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70 transition-colors"
                      data-testid="video-thumbnail-remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="mb-3 rounded-xl border-2 border-dashed border-border/50 bg-cream-50 h-24 flex flex-col items-center justify-center text-charcoal/40">
                    <Image className="w-6 h-6 mb-1" />
                    <span className="text-xs">No thumbnail</span>
                  </div>
                )}
                {/* Upload button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                  data-testid="video-thumbnail-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full rounded-xl h-11 gap-2"
                  data-testid="video-thumbnail-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {currentVideo.thumbnail_url ? 'Replace Thumbnail' : 'Upload Thumbnail'}
                    </>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Category</label>
                  <Select
                    value={currentVideo.category}
                    onValueChange={(value) => setCurrentVideo({ ...currentVideo, category: value })}
                  >
                    <SelectTrigger className="h-11 bg-cream-50 border-border/50 rounded-xl" data-testid="video-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Duration</label>
                  <Input
                    value={currentVideo.duration || ''}
                    onChange={(e) => setCurrentVideo({ ...currentVideo, duration: e.target.value })}
                    placeholder="10:30"
                    className="h-11 bg-cream-50 border-border/50 rounded-xl"
                    data-testid="video-duration-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Status</label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentVideo({ ...currentVideo, published: !currentVideo.published })}
                  className={`w-full h-11 rounded-xl gap-2 ${
                    currentVideo.published 
                      ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' 
                      : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                  }`}
                  data-testid="video-status-toggle"
                >
                  {currentVideo.published ? (
                    <>
                      <Eye className="w-4 h-4" />
                      Published
                    </>
                  ) : (
                    <>
                      <EyeOff className="w-4 h-4" />
                      Draft
                    </>
                  )}
                </Button>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Description</label>
                <Textarea
                  value={currentVideo.description}
                  onChange={(e) => setCurrentVideo({ ...currentVideo, description: e.target.value })}
                  placeholder="Describe what this video covers..."
                  rows={4}
                  className="bg-cream-50 border-border/50 rounded-xl resize-none"
                  data-testid="video-description-input"
                />
              </div>
            </div>

            <div className="p-6 border-t border-border/50 flex justify-end gap-3 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={closeEditor} className="rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !currentVideo.title || !currentVideo.video_url}
                className="bg-sage-600 hover:bg-sage-700 text-white rounded-xl gap-2"
                data-testid="save-video-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Video
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminVideos;
